import React, { createContext, useContext, useCallback } from "react";
import { CheckCircle, XCircle, X } from "lucide-react";

// Create context
const ToastContext = createContext(null);

// Custom hook for using toast
export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error("useToast must be used within a ToastProvider");
    }
    return context;
};

// Toast component
const Toast = ({ message, type, onClose }) => {
    return (
        <div className="fixed top-4 right-4 z-50 animate-fade-in transition-all duration-300">
            <div
                className={`
        flex items-center gap-2 p-4 rounded-lg shadow-lg min-w-[320px]
        ${type === "success" ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}
      `}
            >
                {type === "success" ? <CheckCircle className="w-5 h-5 text-green-500" /> : <XCircle className="w-5 h-5 text-red-500" />}

                <p className="flex-1 text-sm font-medium">{message}</p>

                <button
                    onClick={onClose}
                    className={`
            p-1 rounded-full hover:bg-opacity-20
            ${type === "success" ? "hover:bg-green-200" : "hover:bg-red-200"}
          `}
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

// Toast provider component
export const ToastProvider = ({ children }) => {
    const [toast, setToast] = React.useState({
        visible: false,
        message: "",
        type: "success",
    });

    const showToast = useCallback((message, type = "success") => {
        setToast({ visible: true, message, type });

        // Auto dismiss after 2 seconds
        setTimeout(() => {
            setToast((prev) => ({ ...prev, visible: false }));
        }, 3000);
    }, []);

    const hideToast = useCallback(() => {
        setToast((prev) => ({ ...prev, visible: false }));
    }, []);

    return (
        <ToastContext.Provider value={showToast}>
            {children}
            {toast.visible && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        </ToastContext.Provider>
    );
};

export default ToastProvider;
